import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import makeConstants from 'lib/makeConstants';
import {callTargetedAction, getActiveState, getStoreByName} from 'client/framework';
import DrawerStore from './DrawerStore';
import {drawerActions} from './DrawerActions';

import {setIntercomAlignment} from 'vendor/Intercom/browser/utils';
import {Icon} from '@albert-io/atomic';

import './drawer.scss';

/**
 * <Drawer
 *   storeName='MyDrawerStore'
 *   className='my-drawer-class'
 *   drawerTitle='My Drawer'
 *   isOpen={false}
 *   onOpen={() => {
 *     callAction(randomActions.RANDOM_ACTION);
 *   }}
 * >
 *   <div>
 *     I'm a div in a Drawer!
 *   </div>
 * </Drawer>
 */

/**
 * Light: Current default -- Used in global nav drawer
 * Dark: Inverted header and body colors --- Used in Add to Assignment template
 * Blue: Currently unused -- To be added for Experimental Nav
 */
export const colorSchemes = makeConstants('LIGHT', 'DARK', 'BLUE');

export default class Drawer extends React.Component {
  static propTypes = {
    storeName: PropTypes.string,
    drawerContentClassName: PropTypes.string,
    drawerTitle: PropTypes.string.isRequired,
    drawerFooterContents: PropTypes.node,
    hasCloseButton: PropTypes.bool,
    hasTopCloseButton: PropTypes.bool,
    isOpen: PropTypes.bool,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    colorScheme: PropTypes.string,
    direction: PropTypes.string,
    className: PropTypes.string,
    headerClassName: PropTypes.string,
    closeOnShadeClick: PropTypes.bool
  };

  static defaultProps = {
    storeName: 'DrawerStore',
    drawerContentClassName: '',
    drawerTitle: '',
    drawerFooterContents: null,
    hasCloseButton: false,
    isOpen: false,
    onOpen: () => {},
    onClose: () => {},
    colorScheme: colorSchemes.LIGHT,
    hasTopCloseButton: true,
    className: '',
    headerClassName: '',
    closeOnShadeClick: true
  };

  constructor(props) {
    super(props);
    this.getStore();
  }

  _isMounted = false;

  componentDidMount() {
    const isOpen = this.props.isOpen;
    if (isOpen) {
      callTargetedAction({
        name: drawerActions.DRAWER_SET_DRAWER_VISIBILITY,
        payload: isOpen,
        targetStore: this.props.storeName
      });
      if (this.props.onOpen) {
        this.props.onOpen();
      }
    }

    this._isMounted = true;
    this._isOpen = this.getStore()?.isOpen();
    // Self-subscribe so the drawer stays reactive to its own store even when a
    // React-Compiler-memoized ancestor cuts the global re-render cascade (#19261).
    getActiveState().on('change', this.handleStoreChange);
    this.toggleBodyClass();
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen !== prevProps.isOpen) {
      callTargetedAction({
        name: drawerActions.DRAWER_SET_DRAWER_VISIBILITY,
        payload: this.props.isOpen,
        targetStore: this.props.storeName
      });
      if (this.props.isOpen && this.props.onOpen) {
        this.props.onOpen();
      } else if (this.props.onClose) {
        this.props.onClose();
      }
    }

    this.toggleBodyClass();
  }

  componentWillUnmount() {
    this._isMounted = false;
    getActiveState().removeListener('change', this.handleStoreChange);
    callTargetedAction({
      name: drawerActions.DRAWER_RESET_STORE,
      targetStore: this.props.storeName
    });
    global.document.body.classList.remove('a-body--drawer-active');
    this.updateIntercomAlignment(false);
  }

  handleStoreChange = () => {
    if (!this._isMounted) {
      return;
    }
    const store = this.getStore();
    if (!store) {
      return;
    }
    const isOpen = store.isOpen();
    if (isOpen !== this._isOpen) {
      this._isOpen = isOpen;
      this.forceUpdate();
    }
  };

  getStore() {
    return getStoreByName(this.props.storeName) || new DrawerStore(this.props.storeName);
  }

  toggleBodyClass() {
    if (this.getStore().isOpen()) {
      global.document.body.classList.add('a-body--drawer-active');
      this.updateIntercomAlignment(true);
    } else {
      global.document.body.classList.remove('a-body--drawer-active');
      this.updateIntercomAlignment(false);
    }
  }

  handleClose(e) {
    e.stopPropagation();
    callTargetedAction({
      name: drawerActions.DRAWER_SET_DRAWER_VISIBILITY,
      payload: false,
      targetStore: this.props.storeName
    });
    if (this.props.onClose) {
      this.props.onClose();
    }
  }

  updateIntercomAlignment(isOpen) {
    const {direction} = this.props;
    // only adjust Intercom alignment if the drawer is on the right side
    if (direction === 'left') {
      return;
    }
    if (isOpen) {
      setIntercomAlignment('left');
    } else {
      setIntercomAlignment('right');
    }
  }

  render() {
    const {hasTopCloseButton, className, headerClassName} = this.props;
    const isOpen = this.getStore().isOpen();
    const drawerShadeClassNames = classnames({
      drawer__shade: true,
      'drawer__shade--hidden': !isOpen
    });
    const drawerClassName = classnames('drawer', className, {
      'drawer--hidden': !isOpen,
      'drawer--light-scheme': this.props.colorScheme === colorSchemes.LIGHT,
      'drawer--dark-scheme': this.props.colorScheme === colorSchemes.DARK,
      'drawer--blue-scheme': this.props.colorScheme === colorSchemes.BLUE
    });

    const closeButton = this.props.hasCloseButton ? (
      <button
        type='button'
        className='drawer-footer__close-btn'
        onClick={(e) => this.handleClose(e)}
      >
        Close
      </button>
    ) : null;

    const drawerWrapperClass = classnames('drawer-wrapper', {
      'drawer-wrapper--left': this.props.direction === 'left'
    });

    return (
      <div className={drawerWrapperClass}>
        <div
          className={drawerShadeClassNames}
          onClick={(e) => {
            e.stopPropagation();
            if (this.props.closeOnShadeClick) {
              callTargetedAction({
                name: drawerActions.DRAWER_SET_DRAWER_VISIBILITY,
                payload: false,
                targetStore: this.props.storeName
              });
              if (this.props.onClose) {
                this.props.onClose();
              }
            }
          }}
        />
        <div className={drawerClassName}>
          <div className={classnames('drawer-header', headerClassName)}>
            <div className='drawer-header__title'>{this.props.drawerTitle}</div>
            {hasTopCloseButton && (
              <div className='drawer-header__close-btn' onClick={(e) => this.handleClose(e)}>
                <Icon icon='xmark' />
              </div>
            )}
          </div>
          <div className={`drawer-content ${this.props.drawerContentClassName}`}>
            {/* Only render interactive content when drawer is open for WCAG compliance */}
            {isOpen ? this.props.children : null}
          </div>
          <div className='drawer-footer'>
            {/* Only render footer content when drawer is open for WCAG compliance */}
            {isOpen ? closeButton : null}
            {isOpen ? this.props.drawerFooterContents : null}
          </div>
        </div>
      </div>
    );
  }
}
